import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { donationService, adminService } from '../../services';
import { formatCurrency, formatNumber, toNepaliNumerals } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';

const Reports = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [statsRes, analyticsRes] = await Promise.all([
                adminService.getFinancialStats(),
                adminService.getDonationAnalytics()
            ]);
            setStats(statsRes?.data || statsRes);
            setAnalytics(analyticsRes?.data || analyticsRes);
        } catch (err) {
            console.error('Error fetching initial report data:', err);
            try {
                const publicStats = await donationService.getPublicStats();
                setStats(publicStats?.data || publicStats);
            } catch (pErr) {
                console.error('Error fetching public stats:', pErr);
                setError('Failed to load summary statistics');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading && !stats) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                    <p className="text-gray-500 font-medium">{t('reports.loadingData')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-12">
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">{t('reports.title')}</h1>
                    <p className="mt-3 text-lg text-gray-600 max-w-2xl">
                        {t('reports.description')}
                    </p>
                </div>

                {error && (
                    <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-lg shadow-sm">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                            {error}
                        </div>
                    </div>
                )}

                {/* Section: Public Financials */}
                <div className="mb-6 flex items-center gap-2">
                    <div className="h-8 w-1 bg-primary-600 rounded-full"></div>
                    <h2 className="text-xl font-bold text-gray-800 uppercase tracking-wider">{t('reports.financialOverview')}</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-12">
                    <ReportStatCard
                        title={t('reports.totalRaised')}
                        value={formatCurrency(stats?.totalRaised || 0, lang)}
                        subtitle={t('reports.impactOfEveryRupee')}
                        color="blue"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    />
                    <ReportStatCard
                        title={t('reports.donationCount')}
                        value={formatNumber(analytics?.totalDonations || stats?.totalDonations || 0, lang)}
                        subtitle={t('reports.totalTransactions')}
                        color="indigo"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>}
                    />
                    <ReportStatCard
                        title={t('reports.averageDonation')}
                        value={formatCurrency(analytics?.averageDonation || 0, lang)}
                        subtitle={t('reports.contributionPerDonor')}
                        color="emerald"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                    />
                    <ReportStatCard
                        title={t('reports.successRate')}
                        value={`${lang === 'ne' ? toNepaliNumerals(stats?.successRate || analytics?.successRate || 0) : (stats?.successRate || analytics?.successRate || 0)}%`}
                        subtitle={t('reports.systemReliability')}
                        color="amber"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                    {/* Distribution Chart */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-900">{t('reports.donationDistribution')}</h3>
                            <p className="text-sm text-gray-500">{t('reports.distributionDescription')}</p>
                        </div>
                        <div className="space-y-6">
                            <DistributionMetric
                                label={t('reports.partyFund')}
                                value={analytics?.partyTotal || stats?.party || 0}
                                total={stats?.totalRaised || 1}
                                color="bg-primary-600"
                                lang={lang}
                            />
                            <DistributionMetric
                                label={t('reports.candidateFund')}
                                value={analytics?.candidateTotal || stats?.candidatesTotal || 0}
                                total={stats?.totalRaised || 1}
                                color="bg-orange-500"
                                lang={lang}
                            />
                        </div>
                    </div>

                    {/* Geographic Breakdown */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-900">{t('reports.geographicSpread')}</h3>
                            <p className="text-sm text-gray-500">{t('reports.geographicDescription')}</p>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
                            {(analytics?.demographics?.byProvince || []).map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                                        <span className="font-semibold text-gray-700 capitalize">{item.province}</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-900">{item.count} {t('reports.donors')}</span>
                                </div>
                            ))}
                            {(!analytics?.demographics?.byProvince || analytics.demographics.byProvince.length === 0) && (
                                <div className="text-center py-8 text-gray-400 italic">{t('reports.noGeographicData')}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Section: Donor Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <ReportStatCard
                        title={t('reports.currentTopDonor')}
                        value={<span className="capitalize">{analytics?.topDonor?.name || t('donations.anonymous')}</span>}
                        subtitle={analytics?.topDonor?.amount ? `${t('reports.contributing')} ${formatCurrency(analytics.topDonor.amount, lang)}` : t('reports.platformContribution')}
                        color="rose"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-7.714 2.143L11 21l-2.286-6.857L1 12l7.714-2.143L11 3z" /></svg>}
                    />
                    <ReportStatCard
                        title={t('reports.recentMomentum')}
                        value={formatNumber(analytics?.recentDonationsCount || 0, lang)}
                        subtitle={t('reports.last7Days')}
                        color="fuchsia"
                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                    />
                </div>

                {/* Admin Only Section */}
                {user?.role === 'ADMIN' && (
                    <div className="mt-16 pt-12 border-t border-gray-200">
                        <div className="mb-6 flex items-center gap-2">
                            <div className="h-8 w-1 bg-red-600 rounded-full"></div>
                            <h2 className="text-xl font-bold text-gray-800 uppercase tracking-wider">{t('reports.adminOverview')}</h2>
                        </div>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            <ReportStatCard
                                title={t('reports.platformUsers')}
                                value={formatNumber(stats?.totalUsers || 0, lang)}
                                subtitle={t('reports.registeredAccounts')}
                                color="purple"
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                            />
                            <ReportStatCard
                                title={t('reports.pendingKyc')}
                                value={formatNumber(stats?.pendingKyc || 0, lang)}
                                subtitle={t('reports.pendingVerifications')}
                                color="red"
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
                                link="/admin/users"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Reusable Components
const ReportStatCard = ({ title, value, subtitle, icon, link, color = 'primary' }) => {
    const colorStyles = {
        blue: 'bg-blue-50 text-blue-600 border-blue-100',
        indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
        emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
        amber: 'bg-amber-50 text-amber-600 border-amber-100',
        rose: 'bg-rose-50 text-rose-600 border-rose-100',
        fuchsia: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100',
        purple: 'bg-purple-50 text-purple-600 border-purple-100',
        red: 'bg-red-50 text-red-600 border-red-100',
        primary: 'bg-primary-50 text-primary-600 border-primary-100'
    };

    const cardContent = (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl border ${colorStyles[color] || colorStyles.primary} shadow-sm transition-transform group-hover:scale-110`}>
                    {icon}
                </div>
                {link && (
                    <div className="text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </div>
                )}
            </div>
            <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">{title}</p>
                <p className="text-3xl font-black text-gray-900 tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">{value}</p>
                <p className="text-sm text-gray-500 mt-2 font-medium line-clamp-1">{subtitle}</p>
            </div>
        </div>
    );

    if (link) {
        return (
            <Link to={link} className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                {cardContent}
            </Link>
        );
    }

    return (
        <div className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
            {cardContent}
        </div>
    );
};

const DistributionMetric = ({ label, value, total, color, lang = 'en' }) => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-bold text-gray-700">{label}</span>
                <span className="text-sm font-black text-gray-900">{lang === 'ne' ? toNepaliNumerals(percentage) : percentage}% ({formatCurrency(value, lang)})</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className={`${color} h-3 rounded-full transition-all duration-1000 ease-out`} style={{ width: `${percentage}%` }}></div>
            </div>
        </div>
    );
};

export default Reports;
